import classNames from 'classnames';
import React, { memo, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { areEqual, FixedSizeList, ListChildComponentProps } from 'react-window';
import Box from 'ui-box';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  Button,
  Card,
  Checkbox,
  EditableText,
  Heading,
  majorScale,
  Popover,
  Spinner,
  useToast,
} from '../../../npaper';
import { ProductThumbnail } from '../../products/ProductThumbnail';

import { ICompany, useStores } from '../../../stores';
import { ImportDataItemType, ImportDataType } from './ImportCSVFile';
import { ProductGroupTitle } from '../ProductGroupTitle';

const PreviewImport = ({
  importData,
  supplier,
  currency,
  onBack,
  onSuccess,
}: {
  importData?: ImportDataType;
  currency?: string;
  supplier?: ICompany;
  onBack: () => void;
  onSuccess: () => void;
}) => {
  const intl = useIntl();
  const toaster = useToast();
  const { productStore } = useStores();

  const [skipLines, setSkipLines] = useState<{
    [key: number]: boolean;
  }>({});

  const [performingImport, setPerformingImport] = useState(false);

  const reset = () => {
    setSkipLines({});
    setPerformingImport(false);
  };

  const performImport = async () => {
    if (!importData) {
      toaster.addToast({
        intent: 'warning',
        message: intl.formatMessage({
          defaultMessage: 'Could not extract data to import',
        }),
      });
      return;
    }
    setPerformingImport(true);
    const toastId = toaster.addToast({
      intent: 'info',
      message: intl.formatMessage({
        defaultMessage: 'Importing products. Please wait.',
      }),
      timeout: 100000,
    });
    try {
      const filteredItems = importData.items.filter((d, i) => !skipLines[i]);
      const data = await productStore.performImport({
        ...importData,
        items: filteredItems,
      });
      toaster.addToast({
        intent: 'success',
        message: intl.formatMessage(
          {
            defaultMessage:
              'Import complete. Created {createdCount, plural, =1 {# product} other {# products}}, updated {updatedCount, plural, =1 {# product} other {# products}} and archived {archivedCount, plural, =1 {# product} other {# products}}',
          },
          {
            createdCount: data.products_created.length,
            updatedCount: data.products_updated.length,
            archivedCount: data.products_archived.length,
          }
        ),
        timeout: 10000,
      });

      onSuccess();
      reset();
    } catch (err) {
      console.error(err);
      toaster.addToast({
        intent: 'danger',
        message: intl.formatMessage({
          defaultMessage: 'Could not perform import. Please try again.',
        }),
      });
    }
    toaster.removeToast(toastId);
    setPerformingImport(false);
  };

  if (!importData || !currency) {
    return <></>;
  }

  return (
    <Box height="444px" maxWidth="100%" marginX="auto" paddingY={majorScale(4)}>
      <Box className="mb-2" marginX="auto" maxWidth="800px">
        <Heading size={300}>
          <FormattedMessage
            defaultMessage="Review import"
            description="Heading for reviewing import data"
          />
        </Heading>
        <Box is="p" className="mt-2" maxWidth="400px">
          <FormattedMessage
            defaultMessage="Make sure that the import will be performed correctly."
            description="Description for reviewing import data"
          />
        </Box>
      </Box>

      <Card
        flexGrow={1}
        background="white"
        display="block"
        height="100%"
        width="100%"
        marginX="auto"
      >
        <Box
          display="grid"
          gridTemplateColumns="40px repeat(auto-fit, minmax(1px, 1fr));"
          paddingX={majorScale(2)}
          className="border-bottom"
          alignItems="center"
          height={48}
        >
          <div />
          <Box is="p">Product</Box>
          <Box is="p">SKU</Box>
          <Box is="p">Manufacturer number</Box>
          <Box is="p">Name</Box>
          <Box is="p">Product group</Box>
          <Box is="p">Description</Box>
          <Box is="p" textAlign="right">
            Stock
          </Box>
          <Box is="p" textAlign="right">
            Cost price
          </Box>
        </Box>
        <AutoSizer disableWidth>
          {({ height }) => (
            <FixedSizeList
              height={height - 4 - 48}
              width="100%"
              itemSize={48}
              itemCount={importData.items.length}
              itemData={importData}
            >
              {memo(
                (props) => (
                  <ImportRowRenderer
                    {...props}
                    currency={currency}
                    skip={skipLines[props.index]}
                    onSkipChanged={(flag) =>
                      setSkipLines((s) => ({
                        ...s,
                        [props.index]: !flag,
                      }))
                    }
                  />
                ),
                areEqual
              )}
            </FixedSizeList>
          )}
        </AutoSizer>
      </Card>

      <Box marginX="auto" maxWidth="800px">
        <Box paddingBottom={majorScale(4)}>
          <p className="small muted mt-2">
            <FormattedMessage
              defaultMessage="Displaying {rows, plural, one {# row} other {# rows} }"
              values={{ rows: importData.items.length }}
            />
          </p>
        </Box>
        <Box>
          <Heading size={300}>
            <FormattedMessage defaultMessage="Summary" />
          </Heading>
          <Box is="p" className="mt-2" maxWidth="400px">
            <FormattedMessage
              defaultMessage="Creating {products, plural, =1 {# product} other {# products}}"
              values={{
                products: importData.items.filter((item) => !item.product)
                  .length,
              }}
            />
          </Box>
          <Box is="p" className="mt-2" maxWidth="400px">
            <FormattedMessage
              defaultMessage="Updating {products, plural, =1 {# product} other {# products}}"
              values={{
                products: importData.items.filter((item) => item.product)
                  .length,
              }}
            />
          </Box>
          <Box display="flex" marginTop={majorScale(2)}>
            <Button
              iconLeft="arrow-left"
              onClick={() => {
                reset();
                onBack();
              }}
            >
              <FormattedMessage defaultMessage="Back" />
            </Button>
            <Button
              primary
              className="ml-2"
              isLoading={performingImport}
              iconRight="import"
              onClick={async () => {
                performImport();
              }}
            >
              <FormattedMessage
                defaultMessage="Perform import"
                description="Import primary action - in sidesheet title"
              />
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ImportRowRenderer = (
  props: ListChildComponentProps & {
    skip: boolean;
    onSkipChanged: (flag: boolean) => void;
    currency: string;
  }
) => {
  const { productStore } = useStores();
  const item: ImportDataItemType = props.data.items[props.index];

  return (
    <Box
      style={props.style}
      display="grid"
      gridTemplateColumns="40px repeat(auto-fit, minmax(1px, 1fr));"
      className={classNames(
        'px-8 border-bottom',
        props.skip && 'muted bg-gray-50'
      )}
      borderBottomWidth={1}
      alignItems="center"
    >
      <Checkbox checked={!props.skip} onChange={props.onSkipChanged} />
      <Box>
        {item.product &&
          (productStore.getItemNaive(item.product) ? (
            <ProductThumbnail
              disableLink
              product={productStore.getItemNaive(item.product)!}
              showSKU
            />
          ) : (
            <Spinner size={16} />
          ))}
      </Box>
      <Box className="elipsis">{item.sku}</Box>
      <Box className="elipsis">{item.manufacturer_number}</Box>
      <Box className="elipsis">
        <Popover
          placement="bottom-start"
          offset={-24}
          content={(close) => (
            <Card backgroundColor="white" elevation={2} border="none">
              <EditableText autoFocus value={item.name} autoHeight />
            </Card>
          )}
        >
          <Box className="elipsis" style={{ cursor: 'pointer' }}>
            {item.name}
          </Box>
        </Popover>
      </Box>
      <Box className="elipsis">
        {item.group && <ProductGroupTitle productGroupId={item.group} />}
      </Box>
      <Box className="elipsis">
        <Popover
          placement="bottom-start"
          offset={-24}
          content={(close) => (
            <Card backgroundColor="white" elevation={2} border="none">
              {item.description}
            </Card>
          )}
        >
          <Box className="elipsis" style={{ cursor: 'pointer' }}>
            {item.description}
          </Box>
        </Popover>
      </Box>
      <Box textAlign="right">
        {item.stock === null || item.stock === undefined ? (
          ''
        ) : (
          <FormattedNumber format="unit" value={item.stock || 0} />
        )}
      </Box>
      <Box textAlign="right">
        {item.cost_price === null || item.cost_price === undefined ? (
          ''
        ) : (
          <FormattedNumber
            format="currency"
            currency={props.currency}
            value={item.cost_price || 0}
          />
        )}
      </Box>
    </Box>
  );
};

export default PreviewImport;
